import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Users, UserPlus, Shield, Eye, Trash2, Edit, X, Save } from 'lucide-react';

interface AppUser {
  id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'user' | 'readonly';
  permissions: any;
  phone_agent?: boolean;
  created_at: string;
  updated_at: string;
}

const AVAILABLE_PERMISSIONS = [
  { key: 'warranty', label: 'Warranty System', icon: '🛡️' },
  { key: 'call_center', label: 'Call Center', icon: '📞' },
  { key: 'contact_center', label: 'Contact Center', icon: '☎️' },
  { key: 'monitoring', label: 'System Monitoring', icon: '🔍' },
  { key: 'errors', label: 'Error Tracking', icon: '🐛' },
  { key: 'analytics', label: 'Analytics', icon: '📊' },
  { key: 'performance', label: 'Performance', icon: '⚡' },
  { key: 'plans', label: 'Plans Manager', icon: '📋' },
  { key: 'customers', label: 'Customers', icon: '🏢' },
  { key: 'reviews', label: 'Reviews', icon: '💬' },
  { key: 'imports', label: 'Data Import', icon: '📥' },
  { key: 'integrations', label: 'Integrations', icon: '🔌' },
  { key: 'sales', label: 'Sales & Revenue', icon: '💰' },
  { key: 'memberships', label: 'Memberships', icon: '👥' },
  { key: 'traffic', label: 'Traffic & Funnels', icon: '📈' },
  { key: 'conversions', label: 'Conversions', icon: '🎯' },
  { key: 'api', label: 'API Diagnostics', icon: '🔧' },
  { key: 'renewals', label: 'Renewals', icon: '🔄' },
  { key: 'audit', label: 'Audit Log', icon: '📜' },
  { key: 'users', label: 'Users & Roles', icon: '👤' },
  { key: 'exports', label: 'Exports', icon: '📤' },
  { key: 'settings', label: 'Settings', icon: '⚙️' },
];

export default function UsersPage() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user' as 'admin' | 'user' | 'readonly',
    permissions: {} as Record<string, boolean>,
    phone_agent: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      // Call edge function to create user (uses service role key)
      const { data: session } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session?.access_token}`
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: formData.role,
          permissions: formData.role === 'admin' ? {} : formData.permissions,
          phone_agent: formData.phone_agent
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create user');
      }

      // Reset form and reload
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'user',
        permissions: {},
        phone_agent: false
      });
      setShowCreateForm(false);
      loadUsers();
      alert('User created successfully!');
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const updateUser = async (userId: string, updates: Partial<AppUser>) => {
    try {
      const { error } = await supabase
        .from('app_users')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;
      loadUsers();
      setEditingUser(null);
      alert('User updated successfully!');
    } catch (error: any) {
      console.error('Error updating user:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deleteUser = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to delete user ${email}? This will also delete their auth account.`)) return;

    try {
      // Delete from app_users
      const { error: dbError } = await supabase
        .from('app_users')
        .delete()
        .eq('id', userId);

      if (dbError) throw dbError;

      // Delete from auth
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) console.warn('Auth deletion warning:', authError);

      loadUsers();
      alert('User deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role === 'user').length;
  const readonlyCount = users.filter(u => u.role === 'readonly').length;

  return (
    <AdminPageLayout
      title="👥 Users & Roles"
      subtitle="Manage admin accounts and page-level permissions"
      actions={
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-white flex items-center space-x-2"
        >
          <UserPlus className="w-4 h-4" />
          <span>Create User</span>
        </button>
      }
    >
      {/* Stats */}
      <StatsGrid cols={4}>
        <StatCard label="Total Users" value={users.length} icon={Users} />
        <StatCard label="Admins" value={adminCount} icon={Shield} variant="success" />
        <StatCard label="Custom Users" value={userCount} icon={Users} />
        <StatCard label="Read-Only" value={readonlyCount} icon={Eye} />
      </StatsGrid>

      {/* Users Table */}
      <div className="bg-slate-900 rounded-lg border border-slate-800 overflow-hidden mt-6">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="p-4 text-left text-slate-300 font-semibold">Name</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Email</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Role</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Phone Agent</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Permissions</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Created</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading users...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">No users found</td></tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="p-4 text-slate-300 font-medium">{user.full_name || 'N/A'}</td>
                  <td className="p-4 text-slate-300">{user.email}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      user.role === 'admin' ? 'bg-blue-900 text-blue-400' :
                      user.role === 'user' ? 'bg-green-900 text-green-400' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      user.phone_agent ? 'bg-green-900 text-green-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {user.phone_agent ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {user.role === 'admin' ? (
                      <span className="text-blue-400">Full Access</span>
                    ) : user.role === 'readonly' ? (
                      <span className="text-slate-500">View Only</span>
                    ) : (
                      <span>{Object.keys(user.permissions || {}).filter(k => user.permissions[k]).length} pages</span>
                    )}
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteUser(user.id, user.email)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-200">Create New User</h2>
              <button onClick={() => setShowCreateForm(false)} className="text-slate-400 hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={createUser} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value as any})}
                  className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="admin">Admin (Full Access)</option>
                  <option value="user">User (Custom Permissions)</option>
                  <option value="readonly">Read-Only (View Only)</option>
                </select>
                <p className="text-sm text-slate-400 mt-1">
                  {formData.role === 'admin' ? 'Full access to all pages and features' :
                   formData.role === 'user' ? 'Custom page-level permissions (configure below)' :
                   'Can view data but cannot make changes'}
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-3 p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-750 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.phone_agent}
                    onChange={(e) => setFormData({...formData, phone_agent: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-slate-200 font-medium">📞 Phone Agent</span>
                </label>
                <p className="text-sm text-slate-400 mt-2">
                  Enable this to make user available for call queues and show in agent filters
                </p>
              </div>

              {/* Permission Toggles (only for 'user' role) */}
              {formData.role === 'user' && (
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Page Permissions</h3>
                  <p className="text-sm text-slate-400 mb-4">Select which pages this user can access:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {AVAILABLE_PERMISSIONS.map((perm) => (
                      <label key={perm.key} className="flex items-center space-x-3 p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-750 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.permissions[perm.key]}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              [perm.key]: e.target.checked
                            }
                          })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-slate-200 flex-1">
                          {perm.icon} {perm.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-4 p-3 bg-blue-900/20 border border-blue-800 rounded">
                    <p className="text-sm text-blue-300">
                      💡 Tip: Admin role grants full access automatically. Use "user" role for granular control.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold disabled:bg-slate-600 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Create User</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-200">Edit User: {editingUser.email}</h2>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                <input
                  type="text"
                  value={editingUser.full_name || ''}
                  onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                  className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Role *</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value as any})}
                  className="w-full p-3 bg-slate-800 text-slate-200 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                >
                  <option value="admin">Admin (Full Access)</option>
                  <option value="user">User (Custom Permissions)</option>
                  <option value="readonly">Read-Only (View Only)</option>
                </select>
              </div>

              <div>
                <label className="flex items-center space-x-3 p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-750 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingUser.phone_agent}
                    onChange={(e) => setEditingUser({...editingUser, phone_agent: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-slate-200 font-medium">📞 Phone Agent</span>
                </label>
                <p className="text-sm text-slate-400 mt-2">
                  Enable this to make user available for call queues and show in agent filters
                </p>
              </div>

              {/* Permission Toggles */}
              {editingUser.role === 'user' && (
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Page Permissions</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {AVAILABLE_PERMISSIONS.map((perm) => (
                      <label key={perm.key} className="flex items-center space-x-3 p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-750 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!editingUser.permissions?.[perm.key]}
                          onChange={(e) => setEditingUser({
                            ...editingUser,
                            permissions: {
                              ...editingUser.permissions,
                              [perm.key]: e.target.checked
                            }
                          })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-slate-200 flex-1">
                          {perm.icon} {perm.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateUser(editingUser.id, {
                    full_name: editingUser.full_name,
                    role: editingUser.role,
                    permissions: editingUser.role === 'admin' ? {} : editingUser.permissions,
                    phone_agent: editingUser.phone_agent
                  })}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
}
